import React, { useContext, useEffect, useState } from 'react'
import { css } from '@emotion/core'
import { Formik, FormikProps } from 'formik'
import { colors, Filter, Icon, Input, MessageBox, Spinner, Typography } from '@dw/helix'

import { API } from '../../utils/api'
import isObjectEmpty from '../../utils/isObjectEmpty'

import { ArticlesContext } from '../../contexts'
import { Pagination } from '../../components'
import ArticleForm from './ArticleForm'
import { MissingValuesPayload } from '../GenerateSerialNumbersPage/MissingValuesForm'

interface ManageCodesPageProps {
  history: object
  location: object
  match: object
}

interface MessageProps {
  type?: 'error' | 'success' | 'primary'
  text?: string
}

const pageStyles = css`
  margin: -5px -64px 0;
  @media (max-width: 1199px) {
    margin-left: 0;
    margin-right: 0;
  }
`

const panelStyles = css`
  position: relative;
  display: flex;
  flex-direction: column;
  background-color: #fff;
  margin-top: 20px;
  padding: 30px 36px;
  margin-bottom: 40px;
  border-radius: 2px;
  box-shadow: 0 1px 7px 0 rgba(0, 0, 0, 0.19), 0 3px 3px 0 rgba(0, 0, 0, 0.13);

  &::after {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    background-color: rgba(255, 255, 255, 0);
    transition: background-color 0.3s ease;
    top: 0;
    left: 0;
    pointer-events: none;
  }
`

const icoLeftStyles = css`
  margin-bottom: 1px;
`

const searchCaptionStyles = css`
  margin-top: 12px;
  margin-bottom: 12px;
`

const rowStyles = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;

  > *:first-of-type {
    margin-right: 24px;
  }
`
const messageWrapperStyles = css`
  display: flex;
  width: 50%;
  align-items: center;
`

const searchStyles = css`
  display: flex;
  width: 50%;
  align-items: center;
`

const getCodeValue = (compDataMappings: any, articleNo: string, code: string) => {
  if (!compDataMappings[articleNo]) return ''

  if (compDataMappings[articleNo] && !compDataMappings[articleNo][code]) return ''

  return compDataMappings[articleNo][code]
}

const BATCH_CODE_CHARACTERS_AND_NUMBERS_REGEX = /^[A-Z0-9]+$/g
const SERIAL_CODE_NUMBERS_REGEX = /^[A-Z0-9]+$/g

const ManageCodesPage: React.FunctionComponent<ManageCodesPageProps> = props => {
  const [message, setMessage] = useState<MessageProps>({})
  const [page, setPage] = useState(1)
  const [field, setField] = useState('')
  const [ascending, setAscending] = useState(false)
  const [currentArticleNo, setCurrentArticleNo] = useState('')
  const [requestIsProcessing, setProcessing] = useState(false)
  const [missingFilter, setMissingFilter] = useState<'all' | 'missing'>('all')
  const [compDataMappings, setCompDataMappings] = useState<any>({})
  const [articlesEditModeStatus, setArticlesEditModeStatus] = useState<{ [articleNo: string]: boolean }>({})
  const [searchQuery, setSearchQuery] = useState<string>('')
  const { articles, setArticles } = useContext(ArticlesContext)
  useEffect(() => {
    setProcessing(true)
    const fetchArticles = async () => {
      if (articles && !isObjectEmpty(articles)) {
        return
      }
      const { items } = await API.get('its-items', '/', {})
      setArticles(items)
    }

    fetchArticles()

    return () => {}
  }, [articles, setArticles])

  useEffect(() => {
    const getComplementaryData = async () => {
      const response = await API.get('its-comp-data', '/serial-number-generation', {})

      setCompDataMappings(
        response.items.reduce((acc: any, item: any) => {
          acc[item.targetId] = item
          return acc
        }, {}),
      )
      setProcessing(false)
    }

    if (isObjectEmpty(articles)) return

    getComplementaryData()
  }, [articles])

  const ITEMS_PER_PAGE = 7
  const initialValues = { articles: {}, vendors: {} }

  interface SortingFunctions {
    [field: string]: (a: any, b: any) => number
  }

  const sortingFunctions: SortingFunctions = {
    collectionSizeMatrix: (a: any, b: any) => {
      const collectionSizeMatrixA = getCodeValue(compDataMappings, a.SKU, 'collectionSizeMatrix')
      const collectionSizeMatrixB = getCodeValue(compDataMappings, b.SKU, 'collectionSizeMatrix')

      if (collectionSizeMatrixA > collectionSizeMatrixB) return ascending ? 1 : -1

      if (collectionSizeMatrixA < collectionSizeMatrixB) return ascending ? -1 : 1

      return 0
    },
    baseColorMatrix: (a: any, b: any) => {
      const baseColorMatrixA = getCodeValue(compDataMappings, a.SKU, 'baseColorMatrix')
      const baseColorMatrixB = getCodeValue(compDataMappings, b.SKU, 'baseColorMatrix')

      if (baseColorMatrixA > baseColorMatrixB) return ascending ? 1 : -1

      if (baseColorMatrixA < baseColorMatrixB) return ascending ? -1 : 1

      return 0
    },
    secondaryColorMatrix: (a: any, b: any) => {
      const secondaryColorMatrixA = getCodeValue(compDataMappings, a.SKU, 'secondaryColorMatrix')
      const secondaryColorMatrixB = getCodeValue(compDataMappings, b.SKU, 'secondaryColorMatrix')

      if (secondaryColorMatrixA > secondaryColorMatrixB) return ascending ? 1 : -1

      if (secondaryColorMatrixA < secondaryColorMatrixB) return ascending ? -1 : 1

      return 0
    },
    serialCode: (a: any, b: any) => {
      const serialCodeA = getCodeValue(compDataMappings, a.SKU, 'serialCode')
      const serialCodeB = getCodeValue(compDataMappings, b.SKU, 'serialCode')

      if (serialCodeA > serialCodeB) return ascending ? 1 : -1

      if (serialCodeA < serialCodeB) return ascending ? -1 : 1

      return 0
    },
    any: (a: any, b: any) => {
      // @ts-ignore
      if (a[field] > b[field]) return ascending ? 1 : -1

      // @ts-ignore
      if (a[field] < b[field]) return ascending ? -1 : 1

      return 0
    },
  }

  const filteredArticles = Object.values(articles).filter(article => {
    // we do not include non-scannable items
    if (!article.scannable) return false
    // early return search query filter

    if (!article.SKU.toLowerCase().includes(searchQuery.toLowerCase())) return false

    if (missingFilter === 'all') return true

    if (!compDataMappings[article.SKU]) return true

    const { collectionSizeMatrix, baseColorMatrix, secondaryColorMatrix, serialCode } = compDataMappings[article.SKU]
    return !(typeof serialCode === 'string' && typeof collectionSizeMatrix === 'string' && typeof baseColorMatrix === 'string'&& typeof baseColorMatrix === 'string' && typeof secondaryColorMatrix === 'string')
  })

  const sortedArticles = filteredArticles.sort(sortingFunctions[field] || sortingFunctions.any)

  const currentPageArticles = sortedArticles.filter(
    (_, index) => index >= ITEMS_PER_PAGE * (page - 1) && index < ITEMS_PER_PAGE * page,
  )

  const showSuccessMessage = () => {
    setMessage({
      type: 'success',
      text: 'Your changes have been saved successfully',
    })
    window.setTimeout(() => {
      setMessage({})
    }, 3000)
  }

  const showErrorMessage = (errorText: string) => {
    setMessage({
      type: 'error',
      text: errorText,
    })
  }

  const onSortClick = (newField: string, newAscending: boolean) => {
    setField(newField)
    setAscending(newAscending)
  }

  return (
    <div css={pageStyles}>
      <Typography type="h3">Manage batchcode and serialcode</Typography>
      <div css={panelStyles}>
        <div
          css={css`
            display: flex;
            > * {
              margin-right: 12px;
            }
          `}
        >
          <Filter active={missingFilter === 'all'} onClick={() => setMissingFilter('all')}>
            All
          </Filter>
          <Filter active={missingFilter === 'missing'} onClick={() => setMissingFilter('missing')}>
            Missing values
          </Filter>
        </div>
        <Typography type="snippet" css={searchCaptionStyles}>
          Search for article number
        </Typography>
        <div css={rowStyles}>
          <div css={searchStyles}>
            <Input
              value={searchQuery}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
              type="search"
              icoLeft={<Icon name="search" size="s" css={icoLeftStyles} />}
            />
          </div>
          <div css={messageWrapperStyles}>
            {message.type && message.text && (
              <MessageBox
                type={message.type}
                close={true}
                onClick={() => setMessage({ text: '' })}
                css={css`
                  margin-bottom: 0;
                  width: 100%;
                `}
              >
                {message.text}
              </MessageBox>
            )}
          </div>
        </div>
        {!requestIsProcessing && searchQuery.length > 0 && filteredArticles.length === 0 && (
          <Typography type="snippet">
            We couldn’t find any matching results. Please check your spelling and try again.
          </Typography>
        )}
        {missingFilter === 'missing' && (
          <MessageBox
            type="primary"
            css={css`
              margin-top: 12px;
              margin-bottom: 12px;
            `}
          >
            The following article numbers/supply units are missing Batch Code or Serial Code.
          </MessageBox>
        )}

        {currentPageArticles.length !== 0 && (
          <Formik
            validateOnChange={false}
            validate={(values: any) => {
              const errors: any = {}

              if (isObjectEmpty(values.articles[currentArticleNo])) {
                errors.articles = {
                  [currentArticleNo]: {
                    collectionSizeMatrix: 'Required',
                    baseColorMatrix: 'Required',
                    secondaryColorMatrix: 'Required',
                    serialCode: 'Required',
                  },
                }
                showErrorMessage('Please enter only valid characters (0-9, A-Z except for I,O)')
                return errors
              }

              const { collectionSizeMatrix = '', baseColorMatrix = '', secondaryColorMatrix = '', serialCode = '' } = values.articles[currentArticleNo]
              const codeContainsBlackListedChars = (code: string) =>
                ['I', 'O'].some(blacklistedChar => code.includes(blacklistedChar))

              if (
                collectionSizeMatrix &&
                (!collectionSizeMatrix.match(BATCH_CODE_CHARACTERS_AND_NUMBERS_REGEX) || codeContainsBlackListedChars(collectionSizeMatrix))
              ) {
                if (!errors.articles) errors.articles = {}
                if (!errors.articles[currentArticleNo]) errors.articles[currentArticleNo] = {}
                errors.articles[currentArticleNo].batchCode = 'not valid regex'
              }
              if (
                baseColorMatrix &&
                (!baseColorMatrix.match(BATCH_CODE_CHARACTERS_AND_NUMBERS_REGEX) || codeContainsBlackListedChars(baseColorMatrix))
              ) {
                if (!errors.articles) errors.articles = {}
                if (!errors.articles[currentArticleNo]) errors.articles[currentArticleNo] = {}
                errors.articles[currentArticleNo].batchCode = 'not valid regex'
              }
              if (
                secondaryColorMatrix &&
                (!secondaryColorMatrix.match(BATCH_CODE_CHARACTERS_AND_NUMBERS_REGEX) || codeContainsBlackListedChars(secondaryColorMatrix))
              ) {
                if (!errors.articles) errors.articles = {}
                if (!errors.articles[currentArticleNo]) errors.articles[currentArticleNo] = {}
                errors.articles[currentArticleNo].batchCode = 'not valid regex'
              }
              if (
                serialCode &&
                (!serialCode.match(SERIAL_CODE_NUMBERS_REGEX) || codeContainsBlackListedChars(serialCode))
              ) {
                if (!errors.articles) errors.articles = {}
                if (!errors.articles[currentArticleNo]) errors.articles[currentArticleNo] = {}
                errors.articles[currentArticleNo].serialCode = 'not valid regex'
              }

              if (!isObjectEmpty(errors)) {
                showErrorMessage('Please enter only valid characters (0-9, A-Z except for I,O)')
              }
              return errors
            }}
            initialValues={initialValues}
            onSubmit={async (values: any, actions: any) => {
              if (isObjectEmpty(values.articles[currentArticleNo])) {
                actions.setSubmitting(false)
                return
              }

              const { collectionSizeMatrix, baseColorMatrix, secondaryColorMatrix, serialCode } = values.articles[currentArticleNo]

              const payload: MissingValuesPayload = {
                targetId: currentArticleNo,
                targetType: 'ARTICLE',
              }

              if (typeof collectionSizeMatrix === 'string' && collectionSizeMatrix.length > 0) {
                payload.collectionSizeMatrix = collectionSizeMatrix
              }
              if (typeof baseColorMatrix === 'string' && baseColorMatrix.length > 0) {
                payload.baseColorMatrix = baseColorMatrix
              }
              if (typeof secondaryColorMatrix === 'string' && secondaryColorMatrix.length > 0) {
                payload.secondaryColorMatrix = secondaryColorMatrix
              }
              if (typeof serialCode === 'string' && serialCode.length > 0) {
                payload.serialCode = serialCode
              }

              try {
                const response = await API.post('its-comp-data', '/serial-number-generation', { body: payload })

                if (response.message === 'Created') {
                  setArticlesEditModeStatus({ ...articlesEditModeStatus, [currentArticleNo]: false })
                  const newCollectionSizeMatrix =
                    payload.collectionSizeMatrix ||
                    (compDataMappings[currentArticleNo] && compDataMappings[currentArticleNo].collectionSizeMatrix) ||
                    ''
                  const newBaseColorMatrix =
                    payload.baseColorMatrix ||
                    (compDataMappings[currentArticleNo] && compDataMappings[currentArticleNo].baseColorMatrix) ||
                    ''
                  const newSecondaryColorMatrix =
                    payload.secondaryColorMatrix ||
                    (compDataMappings[currentArticleNo] && compDataMappings[currentArticleNo].secondaryColorMatrix) ||
                    ''
                  const newSerialCode =
                    payload.serialCode ||
                    (compDataMappings[currentArticleNo] && compDataMappings[currentArticleNo].serialCode) ||
                    ''

                  setCompDataMappings((mappings: any) => ({
                    ...mappings,
                    [currentArticleNo]: {
                      serialCode: newSerialCode,
                      collectionSizeMatrix: newCollectionSizeMatrix,
                      baseColorMatrix: newBaseColorMatrix,
                      secondaryColorMatrix: newSecondaryColorMatrix,

                    },
                  }))
                  showSuccessMessage()
                }
              } catch (error) {
                const errorMessage =
                  error.response?.data?.message || 'Something went wrong, please contact admin if it persists.'
                showErrorMessage(errorMessage)
              }
              actions.setSubmitting(false)
            }}
          >
            {(formikProps: FormikProps<any>) => (
              <ArticleForm
                {...formikProps}
                currentPageArticles={currentPageArticles}
                compDataMappings={compDataMappings}
                currentArticleNo={currentArticleNo}
                setCurrentArticleNo={setCurrentArticleNo}
                initialValues={initialValues}
                articlesEditModeStatus={articlesEditModeStatus}
                setArticlesEditModeStatus={setArticlesEditModeStatus}
                onSortClick={onSortClick}
              />
            )}
          </Formik>
        )}
        {requestIsProcessing && (
          <Spinner
            color={colors.brandBlue}
            css={css`
              padding: 10px;
            `}
          />
        )}
        {!requestIsProcessing &&
          searchQuery.length === 0 &&
          filteredArticles.length === 0 &&
          missingFilter === 'missing' && (
            <MessageBox
              type="general"
              css={css`
                width: 461px;
              `}
            >
              There are currently no missing values for any data.
            </MessageBox>
          )}
        {filteredArticles.length > 0 && (
          <Pagination
            total={filteredArticles.length}
            perPage={ITEMS_PER_PAGE}
            onChange={setPage}
            css={css`
              margin-top: 25px;
            `}
          />
        )}
      </div>
    </div>
  )
}

export default ManageCodesPage
